import React, { Component, CSSProperties } from 'react';
import svgjs from 'svg.js';
import { GaugeBody } from './body/GaugeBody';
import { GaugeCircularBar } from './circular-bar/GaugeCircularBar';
import { GaugeLabelGroup } from './label-group/GaugeLabelGroup';
import { GaugePropertyValidator } from './property-validator/GaugePropertyValidator';
import { convertSize } from '../../../utils/StyleUtils';
import { defined } from '@unifocus/js-utils';

export interface Svg {
   draw: any;
   get: any;
}

/**
 * {@docCategory Gauge}
 */
export interface GaugeProps {
   /**
    *  each gauge must have unique id between gauges
    */
   id: string;

   /**
    * value indicated by arrow and displayed in center;
    * can be over/under bounds
    */
   value: number;

   /**
    * colors of circular part;
    * number of colors must match number of color percentages;
    * color hex values of 3 or 6 characters;
    * coloring is done from bottom to top
    */
   colors: string[];

   /**
    * number of color percentages must match number of colors;
    * percentages are used to color from bottom to top;
    * sum of percentages must be 1
    */
   colorPercentages: number[];

   /**
    * value showed on top of gauge;
    * @default 100
    */
   upperBound?: number;

   /**
    * value showed on bottom of gauge;
    * @default 0
    */
   lowerBound?: number;

   /**
    * background color of center with arrow;
    * color hex value of 3 or 6 characters;
    * @default '#222222'
    */
   centerBackgroundColor?: string;

   /**
    * color of center label;
    * color hex value of 3 or 6 characters;
    * @default '#ffffff'
    */
   centerLabelColor?: string;

   /**
    * color of left vertical, right horizontal, upper bound and lower bound labels;
    * color hex value of 3 or 6 characters;
    * @default '#222222'
    */
   labelsColor?: string;

   /**
    * show or hide center label;
    * @default true
    */
   displayCenterLabel?: boolean;

   /**
    * show or hide bound labels;
    * @default true
    */
   displayBoundsLabels?: boolean;

   /**
    * value for left vertical label;
    * @default empty string ('')
    */
   leftVerticalLabel?: string;

   /**
    * value for center label;
    * @default value prop
    */
   centerLabel?: string;

   /**
    * value for right horizontal label;
    * @default empty string ('')
    */
   rightHorizontalLabel?: string;

   /**
    * value appended left of center, upper bound and lower bound labels;
    * @default empty string ('')
    */
   prePendCharacter?: string;

   /**
    * value appended right of center, upper bound and lower bound labels;
    * @default empty string ('')
    */
   postPendCharacter?: string;

   /**
    * The minimum width of the gauge. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the gauge. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the gauge. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the gauge. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * name of CSS class
    * @default empty string ('')
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

export interface GaugeProcessedProps {
   id: string;
   value: number;
   colors: string[];
   colorPercentages: number[];
   upperBound: number;
   lowerBound: number;
   centerBackgroundColor: string;
   centerLabelColor: string;
   labelsColor: string;
   displayCenterLabel: boolean;
   displayBoundsLabels: boolean;
   leftVerticalLabel: string;
   centerLabel: string;
   rightHorizontalLabel: string;
   prePendCharacter: string;
   postPendCharacter: string;
}

export class Gauge extends Component<GaugeProps> {
   private svg: Svg = {
      draw: undefined,
      get: undefined
   };
   private svgWidth = 110;
   private svgHeight = 100;
   private svgX = 0;
   private svgY = 0;
   private characterWidthRatio = 6;
   private defaults = {
      upperBound: 100,
      lowerBound: 0,
      centerBackgroundColor: '#222222',
      centerLabelColor: '#ffffff',
      labelsColor: '#222222',
      displayCenterLabel: true,
      displayBoundsLabels: true,
      leftVerticalLabel: '',
      centerLabel: '',
      rightHorizontalLabel: '',
      prePendCharacter: '',
      postPendCharacter: ''
   };

   constructor(props: GaugeProps) {
      super(props);

      GaugePropertyValidator.validate(props);
   }

   shouldComponentUpdate(nextProps: GaugeProps): boolean {
      GaugePropertyValidator.validate(nextProps);
      this.updateSvgWidth(nextProps);

      return true;
   }

   render(): JSX.Element {
      let className = 'unifocus-gauge';
      let gauge: null | JSX.Element = null;

      const style: CSSProperties = {
         maxHeight: convertSize(this.props.maxHeight) || (this.props.style ? this.props.style.maxHeight : undefined),
         maxWidth: convertSize(this.props.maxWidth) || (this.props.style ? this.props.style.maxWidth : undefined),
         ...this.props.style
      };

      if (this.props.minWidth !== undefined && defined(this.props.minWidth)) {
         style.minWidth = convertSize(this.props.minWidth);
      }

      if (this.props.minHeight !== undefined && defined(this.props.minHeight)) {
         style.minHeight = convertSize(this.props.minHeight);
      }

      if (this.svg.draw) {
         gauge = this.drawGauge();
      }

      if (this.props.className) {
         className += " " + this.props.className;
      }

      return (
         <>
            <div id={this.props.id} className={className} style={style}/>
            {gauge}
         </>
      );
   }

   componentDidMount(): void {
      this.createSvg(this.props);
   }

   drawGauge = (): JSX.Element => {
      const props: GaugeProcessedProps = this.processPropsWithDefaultsFilled(this.props);
      const svg: Svg = this.svg;
      const svgId: string = props.id;
      const value: number = props.value;
      const colors: string[] = props.colors;
      const colorPercentages: number[] = props.colorPercentages;

      return (
         <>
            <GaugeBody
               svg={svg}
               svgId={svgId}
               value={value}
               colors={colors}
               colorPercentages={colorPercentages}
               upperBound={props.upperBound}
               lowerBound={props.lowerBound}
               centerBackgroundColor={props.centerBackgroundColor}
            />
            <GaugeCircularBar
               svg={svg}
               svgId={svgId}
               colors={colors}
               colorPercentages={colorPercentages}
            />
            <GaugeLabelGroup
               svg={svg}
               svgId={svgId}
               value={value}
               upperBound={props.upperBound}
               lowerBound={props.lowerBound}
               centerLabelColor={props.centerLabelColor}
               labelsColor={props.labelsColor}
               displayCenterLabel={props.displayCenterLabel}
               displayBoundsLabels={props.displayBoundsLabels}
               leftVerticalLabel={props.leftVerticalLabel}
               centerLabel={props.centerLabel}
               rightHorizontalLabel={props.rightHorizontalLabel}
               prePendCharacter={props.prePendCharacter}
               postPendCharacter={props.postPendCharacter}
            />
         </>
      );
   };

   createSvg = (props: GaugeProps): void => {
      const additionalWidth: number = this.getTextPixelWidthBasedOnTextLengthAndCharacterWidthRatio(props.rightHorizontalLabel || '');

      this.svg.draw = svgjs(props.id).viewbox(this.svgX,
                                            this.svgY,
         this.svgWidth + additionalWidth,
                                            this.svgHeight);
      this.svg.get = svgjs.get;

      this.forceUpdate();
   };

   updateSvgWidth = (props: GaugeProps): void => {
      const svgEl = (document.getElementById(props.id) as any).firstChild;
      const oldViewBoxSettings: string = svgEl.getAttribute('viewBox');
      const newViewBoxSettingsAsStringArray: string[] = oldViewBoxSettings.split(' ');
      const widthPropertyIndex = 2;
      const additionalWidth: number = this.getTextPixelWidthBasedOnTextLengthAndCharacterWidthRatio(props.rightHorizontalLabel || '');
      newViewBoxSettingsAsStringArray[widthPropertyIndex] = (this.svgWidth + additionalWidth).toString(10);

      svgEl.setAttribute('viewBox', newViewBoxSettingsAsStringArray.join(' '));
   };

   getTextPixelWidthBasedOnTextLengthAndCharacterWidthRatio = (text: string): number => {
      return text.length ? (text.length * this.characterWidthRatio) : 0;
   };

   processPropsWithDefaultsFilled = (oldProps: GaugeProps): GaugeProcessedProps => {
      return {
         id: oldProps.id,
         value: oldProps.value,
         colors: [...oldProps.colors].reverse(),
         colorPercentages: [...oldProps.colorPercentages].reverse(),
         upperBound: oldProps.upperBound || this.defaults.upperBound,
         lowerBound: oldProps.lowerBound || this.defaults.lowerBound,
         centerBackgroundColor: oldProps.centerBackgroundColor || this.defaults.centerBackgroundColor,
         centerLabelColor: oldProps.centerLabelColor || this.defaults.centerLabelColor,
         labelsColor: oldProps.labelsColor || this.defaults.labelsColor,
         displayCenterLabel: oldProps.displayCenterLabel || oldProps.displayCenterLabel === undefined && this.defaults.displayCenterLabel,
         displayBoundsLabels: oldProps.displayBoundsLabels || oldProps.displayBoundsLabels === undefined && this.defaults.displayBoundsLabels,
         leftVerticalLabel: oldProps.leftVerticalLabel || this.defaults.leftVerticalLabel,
         centerLabel: oldProps.centerLabel || oldProps.value.toString(),
         rightHorizontalLabel: oldProps.rightHorizontalLabel || this.defaults.rightHorizontalLabel,
         prePendCharacter: oldProps.prePendCharacter || this.defaults.prePendCharacter,
         postPendCharacter: oldProps.postPendCharacter || this.defaults.postPendCharacter
      };
   };
}
